// src/components/VaccineDistributionEstimateForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  Checkbox,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
  faCapsules,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

const VaccineDistributionEstimateForm = () => {
  const navigate = useNavigate();
  const { estimateId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    EstimateID: estimateId || "EST-XXXX-XXX",
    EstimateYear: "",
    EstimateUnit: "",
    DocumentDate: new Date(),
    EstimateBy: profile?.Email || "",
    ContactName: "",
    ContactTel: "",
    ContactEmail: "",
    UsageObjective: "",
    DocumentStatus: "Pending",
    ConfirmationEmailSent: false,
    ConsentGiven: false,
    DLDVBVRemark: "",
    Remark: "",
    Status: 1,
    VaccineDetails: [],
  });

  const [warehouses, setWarehouses] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [newDetail, setNewDetail] = useState({
    VaccineID: "",
    RequestQuantity: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!estimateId);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchDLDOffices = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeList",
        data: { OfficeType: "ปศข." },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOfficeOptions(response.OfficeList || []);
      }
    } catch (error) {
      console.error("Error fetching DLD offices:", error);
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchEstimate = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineDistributionEstimatesByID",
        data: { EstimateID: id },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const estimate = response.VaccineEstimate;
        const details = response.VaccineEstimateDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          const packageSize = vaccine?.PackageSize || 10;
          const requestDoses = detail.RequestDoses || Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            RequestQuantity: detail.RequestQuantity,
            RequestDoses: requestDoses,
          };
        });
        setFormData({
          EstimateID: estimate.EstimateID,
          EstimateYear: estimate.EstimateYear,
          EstimateUnit: estimate.EstimateUnit,
          DocumentDate: new Date(estimate.DocumentDate),
          EstimateBy: estimate.EstimateBy,
          ContactName: estimate.ContactName,
          ContactTel: estimate.ContactTel,
          ContactEmail: estimate.ContactEmail,
          UsageObjective: estimate.UsageObjective,
          DocumentStatus: estimate.DocumentStatus,
          ConfirmationEmailSent: estimate.ConfirmationEmailSent,
          ConsentGiven: estimate.ConsentGiven,
          DLDVBVRemark: estimate.DLDVBVRemark,
          Remark: estimate.Remark,
          Status: estimate.Status,
          VaccineDetails: updatedDetails,
        });
      }
    } catch (error) {
      console.error("Error fetching estimate:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        await fetchWarehouses();
        await fetchDLDOffices();
        const vaccines = await fetchVaccineOptions();
        if (estimateId) {
          await fetchEstimate(estimateId, vaccines);
        }
      }
    };
    loadData();
  }, [sessionId, estimateId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, DocumentDate: date });
  };

  const handleDetailInputChange = (e) => {
    const { name, value } = e.target;
    setNewDetail({ ...newDetail, [name]: value });
  };

  const handleQuantityChange = (vaccineId, value) => {
    const newQuantity = value === "" ? 0 : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId) {
        const packageSize = vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize || 10;
        const newDoses = Math.ceil(newQuantity * packageSize);
        return { ...detail, RequestQuantity: newQuantity, RequestDoses: newDoses };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "EstimateYear":
      case "EstimateUnit":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.EstimateYear &&
      formData.EstimateUnit &&
      Object.keys(errors).length === 0
    );
  };

  const handleAddDetail = () => {
    if (newDetail.VaccineID && newDetail.RequestQuantity > 0) {
      const selectedVaccine = vaccineOptions.find((v) => v.VaccineID === newDetail.VaccineID);
      if (selectedVaccine) {
        const packageSize = selectedVaccine.PackageSize || 10;
        const newQuantity = parseInt(newDetail.RequestQuantity);
        const newDoses = Math.ceil(newQuantity * packageSize);
        const detailData = {
          VaccineID: newDetail.VaccineID,
          name: selectedVaccine.VaccineNameTH,
          RequestQuantity: newQuantity,
          RequestDoses: newDoses,
        };
        setFormData((prevFormData) => ({
          ...prevFormData,
          VaccineDetails: [...prevFormData.VaccineDetails, detailData],
        }));
        setNewDetail({ VaccineID: "", RequestQuantity: "" });
      } else {
        console.warn(`VaccineID ${newDetail.VaccineID} not found in vaccineOptions`);
      }
    } else {
      console.warn("Missing required fields:", newDetail);
    }
  };

  const handleRemoveDetail = (row) => {
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID
      ),
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVaccineDistributionEstimates",
        data: { ...formData, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFormData({ ...formData, EstimateID: response.EstimateID });
        setIsSubmitted(true);
        alert("สร้างเอกสารสำเร็จ! เลขที่เอกสาร: " + response.EstimateID);
      }
    } catch (error) {
      console.error("Error creating estimate:", error);
      alert("เกิดข้อผิดพลาดในการสร้างเอกสาร: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    const date = formData.DocumentDate;
    const formattedDate = date
      ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
          .getDate()
          .toString()
          .padStart(2, "0")}/${date.getFullYear()}`
      : "";
    const submitData = {
      ...formData,
      DocumentDate: formattedDate,
      VaccineDetails: formData.VaccineDetails.map((detail) => ({
        id: detail.VaccineID,
        quantity: detail.RequestQuantity,
        requestDoses: detail.RequestDoses,
      })),
    };

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineDistributionEstimatesByID",
        data: submitData,
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("บันทึกใบประเมินสำเร็จ!");
        navigate("/vaccinedistributionestimatelist");
      }
    } catch (error) {
      console.error("API Error:", error.message);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const isEditable = !isSubmitted;

  const columns = [
    {
      name: <HStack><FontAwesomeIcon icon={faListOl} /><span>รหัสวัคซีน</span></HStack>,
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "200px",
    },
    {
      name: <HStack><FontAwesomeIcon icon={faSyringe} /><span>ชื่อวัคซีน</span></HStack>,
      selector: (row) => row.name,
      sortable: true,
      width: "350px",
    },
    {
      name: <HStack><FontAwesomeIcon icon={faCalculator} /><span>จำนวน (ขวด)</span></HStack>,
      selector: (row) => row.RequestQuantity,
      sortable: true,
      cell: (row) => (
        <Input
          type="number"
          value={row.RequestQuantity}
          onChange={(e) => handleQuantityChange(row.VaccineID, e.target.value)}
          min={0}
          size="sm"
          width="100px"
          ml={10}
        />
      ),
    },
    {
      name: <HStack><FontAwesomeIcon icon={faCapsules} /><span>จำนวนโดส</span></HStack>,
      selector: (row) => row.RequestDoses,
      sortable: true,
      cell: (row) => (
        <Box width="100%" ml={10}>
          {row.RequestDoses.toLocaleString()}
        </Box>
      ),
    },
    {
      name: <HStack><FontAwesomeIcon icon={faGears} /><span>การดำเนินการ</span></HStack>,
      cell: (row) => (
        <IconButton.DeleteIcon onClick={() => handleRemoveDetail(row)} size="sm" />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <Heading as="h2" mb={10} textAlign="center" size={{ base: "md", md: "lg" }}>
        รายการประเมินความต้องการวัคซีนงบจำหน่าย
      </Heading>

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={{ base: 3, md: 5 }} mb={6}>
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
              ข้อมูลเอกสารการประเมิน
            </Heading>
          </HStack>
        </Box>
        <Grid
          templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
          gap={{ base: 4, md: 6 }}
          p={{ base: 3, md: 5 }}
        >
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                เลขที่เอกสาร
              </FormLabel>
              <Input
                name="EstimateID"
                value={formData.EstimateID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                วันที่เอกสาร
              </FormLabel>
              <CustomDatePicker
                value={formData.DocumentDate}
                onChange={handleDateChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl isRequired isInvalid={!!errors.EstimateYear} isDisabled={isSubmitted}>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                ปีงบประมาณ
              </FormLabel>
              <Input
                name="EstimateYear"
                value={formData.EstimateYear}
                onChange={handleInputChange}
                placeholder="เช่น 2566"
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.EstimateYear}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl isRequired isInvalid={!!errors.EstimateUnit} isDisabled={isSubmitted}>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                หน่วยงาน
              </FormLabel>
              <Select
                name="EstimateUnit"
                value={formData.EstimateUnit}
                onChange={handleInputChange}
                placeholder="เลือกหน่วยงาน"
                colorScheme="iBlue"
              >
                {officeOptions.map((office) => (
                  <option key={office.OfficeID} value={office.OfficeName}>
                    {office.OfficeName}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.EstimateUnit}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                ผู้ประเมิน
              </FormLabel>
              <Input
                name="EstimateBy"
                value={formData.EstimateBy}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                ชื่อผู้ติดต่อ
              </FormLabel>
              <Input
                name="ContactName"
                value={formData.ContactName}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                เบอร์โทรผู้ติดต่อ
              </FormLabel>
              <Input
                name="ContactTel"
                value={formData.ContactTel}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                อีเมลผู้ติดต่อ
              </FormLabel>
              <Input
                name="ContactEmail"
                value={formData.ContactEmail}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, sm: 2, lg: 4 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                วัตถุประสงค์การใช้งาน
              </FormLabel>
              <Textarea
                name="UsageObjective"
                value={formData.UsageObjective}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, sm: 2, lg: 4 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                หมายเหตุ - สำนักเทคโนโลยีชีวภัณฑ์สัตว์
              </FormLabel>
              <Textarea
                name="DLDVBVRemark"
                value={formData.DLDVBVRemark}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, sm: 2, lg: 4 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                หมายเหตุ - หน่วยงานที่ประเมิน
              </FormLabel>
              <Textarea
                name="Remark"
                value={formData.Remark}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                สถานะเอกสาร
              </FormLabel>
              <Input
                name="DocumentStatus"
                value={formData.DocumentStatus}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={{ base: 3, md: 5 }} textAlign="right">
            <HStack spacing={5} justify="flex-end">
              <IconButton.Cancel onClick={() => navigate("/vaccinedistributionestimatelist")} />
              <IconButton.Submit label="สร้างเอกสาร" onClick={handleSubmit} />
            </HStack>
          </Box>
        )}
      </Box>

      {isSubmitted && (
        <>
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={{ base: 3, md: 5 }} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iPurple.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faSyringe} color="white" size="xl" />
                <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                  เพิ่มรายการวัคซีนที่ประเมิน
                </Heading>
              </HStack>
            </Box>
            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }}
              gap={{ base: 4, md: 6 }}
              p={{ base: 3, md: 5 }}
            >
              <FormControl>
                <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                  วัคซีน
                </FormLabel>
                <Select
                  name="VaccineID"
                  value={newDetail.VaccineID}
                  onChange={handleDetailInputChange}
                  placeholder="เลือกวัคซีน"
                  colorScheme="iPurple"
                >
                  {vaccineOptions.map((vaccine) => (
                    <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize={{ base: "md", md: "xl" }} color="gray">
                  จำนวน (ขวด)
                </FormLabel>
                <Input
                  name="RequestQuantity"
                  type="number"
                  value={newDetail.RequestQuantity}
                  onChange={handleDetailInputChange}
                  min={0}
                  colorScheme="iPurple"
                />
              </FormControl>
              <Box mt={{ base: 8, md: 10 }}>
                <IconButton.Add onClick={handleAddDetail} h={9} />
              </Box>
            </Grid>
          </Box>

          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={{ base: 3, md: 5 }} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iOrange.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faTable} color="white" size="xl" />
                <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                  รายการวัคซีนที่ประเมิน
                </Heading>
              </HStack>
            </Box>
            <Box p={{ base: 3, md: 5 }} overflowX="auto">
              <DataTable
                columns={columns}
                data={formData.VaccineDetails}
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={customStyles}
              />
            </Box>
          </Box>

          <Box p={{ base: 3, md: 5 }} textAlign="right">
            <HStack spacing={5} justify="flex-end">
              <IconButton.Back
                onClick={() => navigate("/vaccinedistributionestimatelist")}
                label="ย้อนกลับ"
              />
              <IconButton.Save onClick={handleConfirm} isLoading={isLoading} />
            </HStack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VaccineDistributionEstimateForm;